import {
  Check,
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  Loader2,
  Pencil,
  Quote,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ChronologyEvent,
  ChronologyEventOrigin,
  ChronologyReviewStatus,
} from "~/api/model";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  TimelineContent,
  TimelineIndicator,
  TimelineItem,
} from "~/components/ui/timeline";
import { useMobile } from "~/hooks/use-mobile";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import {
  buildTitleText,
  buildWhatLabel,
  shouldShowEyebrow,
} from "../helpers/chronologyDisplay";
import {
  ChronologyFormValues,
  eventToFormValues,
  formatChronologyEventDate,
  formValuesToUpdateRequest,
} from "../helpers/chronologyRequest";
import { useChronologyMutations } from "../hooks/useChronology";
import { useChronologyStore } from "../stores/useChronologyStore";
import {
  ChronologyDuplicateDot,
  ChronologyDuplicateRowInfo,
} from "./ChronologyDuplicateDot";
import { ChronologyEditedBadge } from "./ChronologyEditedBadge";
import { ChronologyEventEditorModal } from "./ChronologyEventEditorModal";
import { ChronologyReviewStatusBadge } from "./ChronologyReviewStatusBadge";
import { ChronologySourceBadge } from "./ChronologySourceBadge";

interface ChronologyEventItemProps {
  event: ChronologyEvent;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes?: HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  /** Duplicate-group marker for this row; omitted when it has no duplicates. */
  duplicateInfo?: ChronologyDuplicateRowInfo;
  /** Opens the review modal scoped to this row's duplicate group. */
  onReviewDuplicates?: (eventIds: string[]) => void;
}

/**
 * A single chronology event: its date, detail and metadata, the source-page
 * citation, override/review affordances, and the row's edit/confirm/reset/
 * delete actions. Opens a modal editor for editing.
 */
export const ChronologyEventItem: FC<ChronologyEventItemProps> = ({
  event,
  dragHandleListeners,
  dragHandleAttributes,
  isDragging = false,
  duplicateInfo,
  onReviewDuplicates,
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const editor = useChronologyStore((state) => state.editor);
  const startEditing = useChronologyStore((state) => state.startEditing);
  const closeEditor = useChronologyStore((state) => state.closeEditor);
  const highlightedEventId = useChronologyStore(
    (state) => state.highlightedEventId
  );
  const setHighlightedEventId = useChronologyStore(
    (state) => state.setHighlightedEventId
  );
  const isHighlighted = highlightedEventId === event.id;

  useEffect(() => {
    if (!isHighlighted) return;
    const timer = setTimeout(() => setHighlightedEventId(null), 2000);
    return () => clearTimeout(timer);
  }, [isHighlighted, setHighlightedEventId]);
  const { update, remove, reset, confirm, isUpdating } =
    useChronologyMutations();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busyAction, setBusyAction] = useState<
    "confirm" | "reset" | "delete" | null
  >(null);

  const isEditing = editor.mode === "editing" && editor.eventId === event.id;
  const isBusy = busyAction !== null;

  const notifyError = (titleId: string, error: unknown) => {
    toast({
      variant: "destructive",
      title: intl.formatMessage({ id: titleId }),
      description:
        error instanceof Error
          ? error.message
          : intl.formatMessage({ id: "chronology.error.unknown" }),
    });
  };

  const handleUpdate = async (values: ChronologyFormValues) => {
    try {
      await update(event.id, formValuesToUpdateRequest(event, values));
      closeEditor();
    } catch (error) {
      notifyError("chronology.error.update", error);
    }
  };

  const handleConfirm = async () => {
    setBusyAction("confirm");
    try {
      await confirm(event.id);
    } catch (error) {
      notifyError("chronology.error.confirm", error);
    } finally {
      setBusyAction(null);
    }
  };

  const handleReset = async () => {
    setBusyAction("reset");
    try {
      await reset(event.id);
    } catch (error) {
      notifyError("chronology.error.reset", error);
    } finally {
      setBusyAction(null);
    }
  };

  const handleDelete = async () => {
    setBusyAction("delete");
    try {
      await remove(event.id);
      setConfirmDeleteOpen(false);
    } catch (error) {
      notifyError("chronology.error.delete", error);
    } finally {
      setBusyAction(null);
    }
  };

  const whatLabel = buildWhatLabel(event);
  const titleText = buildTitleText(event);

  const whoMetadata = [event.provider, event.facility]
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  const documentName = event.documentName.trim();

  const expandedMetadata = [
    event.providerSpecialty.trim() &&
      `${intl.formatMessage({ id: "chronology.expanded.specialty", defaultMessage: "Specialty" })}: ${event.providerSpecialty.trim()}`,
    event.documentType.trim() &&
      `${intl.formatMessage({ id: "chronology.expanded.documentType", defaultMessage: "Document Type" })}: ${event.documentType.trim()}`,
  ].filter(Boolean) as string[];

  const hasQuotes = event.quotes !== null && event.quotes.length > 0;
  const hasExpandedContent = hasQuotes || expandedMetadata.length > 0;

  const displayDate = formatChronologyEventDate(event);
  const isUndated = !event.eventDate;
  const isConfirmed = event.reviewStatus === ChronologyReviewStatus.CONFIRMED;

  return (
    <TimelineItem
      className={cn(
        isDragging && "opacity-50",
        isHighlighted && "animate-chronology-highlight rounded-md"
      )}
    >
      <TimelineIndicator
        variant={isUndated ? "dashed" : "default"}
        className="timeline-grip cursor-grab touch-none active:cursor-grabbing"
        aria-label={intl.formatMessage({
          id: "chronology.action.reorder",
          defaultMessage: "Reorder event",
        })}
        {...dragHandleAttributes}
        {...(dragHandleListeners as Record<string, unknown>)}
      />

      <TimelineContent className="pr-10">
        {/* Eyebrow + Title: clickable to expand */}
        <button
          type="button"
          className="flex w-full items-start gap-1 text-left"
          onClick={() => hasExpandedContent && setExpanded((prev) => !prev)}
          disabled={!hasExpandedContent}
        >
          <div className="flex flex-1 flex-col">
            {shouldShowEyebrow(event) && (
              <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-foreground">
                {whatLabel}
              </span>
            )}
            <p className="text-[13px] leading-[18px] text-foreground">
              {titleText}
            </p>
          </div>
          {hasExpandedContent &&
            (expanded ? (
              <ChevronUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ))}
        </button>

        {/* Status row: date + badges */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="font-mono text-[11px] leading-tight text-muted-foreground">
            {displayDate ?? (
              <FormattedMessage
                id="chronology.list.undatedLabel"
                defaultMessage="No date"
              />
            )}
          </span>
          <ChronologyReviewStatusBadge status={event.reviewStatus} />
          {event.origin === ChronologyEventOrigin.MANUAL && (
            <Badge variant="outline">
              <FormattedMessage
                id="chronology.badge.manual"
                defaultMessage="Manual"
              />
            </Badge>
          )}
          <ChronologyEditedBadge event={event} />
          {duplicateInfo && onReviewDuplicates && (
            <ChronologyDuplicateDot
              colorIndex={duplicateInfo.colorIndex}
              count={duplicateInfo.count}
              rowLabel={[displayDate, titleText]
                .filter((value): value is string => !!value && value.length > 0)
                .join(" — ")}
              onClick={() => onReviewDuplicates(duplicateInfo.eventIds)}
            />
          )}
        </div>

        {/* Who metadata */}
        {whoMetadata.length > 0 && (
          <p className="truncate text-[11px] leading-tight text-muted-foreground">
            {whoMetadata.join(" · ")}
          </p>
        )}

        {/* Source row: trimmed filename + page ref at end */}
        {(documentName.length > 0 ||
          event.reference.pageNumber != null ||
          event.reference.batesNumber) && (
          <div className="flex items-baseline gap-1">
            {documentName.length > 0 && (
              <p className="min-w-0 flex-1 truncate text-[11px] italic leading-tight text-muted-foreground">
                {documentName}
              </p>
            )}
            <ChronologySourceBadge reference={event.reference} />
          </div>
        )}

        {/* Expanded details (shown on click) */}
        {expanded && hasExpandedContent && (
          <div className="mt-1 space-y-2 rounded border border-dashed border-border bg-muted/30 p-2">
            {expandedMetadata.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {expandedMetadata.map((item) => (
                  <span
                    key={item}
                    className="text-[11px] leading-tight text-muted-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
            {hasQuotes && (
              <div className="space-y-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <FormattedMessage
                    id="chronology.expanded.quotes"
                    defaultMessage="Quotes"
                  />
                </span>
                {event.quotes?.map((quote, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-1.5 rounded bg-muted/50 p-1.5"
                  >
                    <Quote className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                    <p className="text-[11px] italic leading-tight text-foreground">
                      {quote.text}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </TimelineContent>

      {/* Kebab actions (top-right) */}
      <div className="absolute right-0 top-0 flex items-start">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-6 w-6 text-muted-foreground",
                isMobile
                  ? "opacity-100"
                  : "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
              )}
              disabled={isBusy}
              aria-label={intl.formatMessage({
                id: "chronology.action.actions",
                defaultMessage: "Actions",
              })}
            >
              {isBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <EllipsisVertical className="h-3.5 w-3.5" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => startEditing(event.id)}>
              <Pencil className="mr-2 h-4 w-4" />
              <FormattedMessage id="chronology.action.edit" />
            </DropdownMenuItem>
            {!isConfirmed && (
              <DropdownMenuItem onClick={handleConfirm}>
                <Check className="mr-2 h-4 w-4" />
                <FormattedMessage id="chronology.action.confirm" />
              </DropdownMenuItem>
            )}
            {event.isEdited &&
              event.origin !== ChronologyEventOrigin.MANUAL && (
                <DropdownMenuItem onClick={handleReset}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  <FormattedMessage id="chronology.action.reset" />
                </DropdownMenuItem>
              )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <FormattedMessage id="chronology.action.delete" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChronologyEventEditorModal
        open={isEditing}
        onOpenChange={(open) => {
          if (!open) closeEditor();
        }}
        mode="edit"
        initialValues={eventToFormValues(event)}
        isSaving={isUpdating}
        onSubmit={handleUpdate}
      />

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <FormattedMessage
                id="chronology.delete.title"
                defaultMessage="Delete Event"
              />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <FormattedMessage
                id="chronology.delete.description"
                defaultMessage="This event will be removed from the chronology. This action cannot be undone."
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyAction === "delete"}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={busyAction === "delete"}
              onClick={handleDelete}
            >
              {busyAction === "delete" && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              <FormattedMessage
                id="chronology.action.delete"
                defaultMessage="Delete"
              />
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TimelineItem>
  );
};
